// src/pages/Accounting/Receipts/components/ReviewMode/ReviewChrome.tsx

import type React from "react";
import { PALETTE } from "../../constants";

/* TOP BAR: progress indicator + close */
interface ReviewTopProps {
  currentIndex: number;
  totalCount: number;
  onClose: () => void;
}
export function ReviewTop({ currentIndex, totalCount, onClose }: ReviewTopProps) {
  const percent =
    totalCount === 0 ? 100 : Math.round(((currentIndex + 1) / totalCount) * 100);
  const safePercent = Math.min(100, Math.max(0, percent));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 24px",
        borderBottom: `1px solid ${PALETTE.line}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          fontSize: 13,
          color: PALETTE.ink2,
          fontWeight: 600,
        }}
      >
        <span>
          Receipt {Math.min(currentIndex + 1, totalCount)} of {totalCount}
        </span>
        <div
          style={{
            width: 160,
            height: 4,
            background: PALETTE.lineSoft,
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${safePercent}%`,
              height: "100%",
              background: PALETTE.vela,
              transition: "width 180ms ease",
            }}
          />
        </div>
        <span style={{ color: PALETTE.ink3, fontWeight: 500 }}>Review queue</span>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Exit review mode"
        style={{
          background: "transparent",
          border: 0,
          color: PALETTE.ink2,
          fontSize: 22,
          lineHeight: 1,
          cursor: "pointer",
          padding: 0,
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ×
      </button>
    </div>
  );
}

/* FOOT BAR: keyboard hints + action buttons */
interface ReviewFootProps {
  onReject: () => void;
  onSkip: () => void;
  onApprove: () => void;
  isLast: boolean;
}
export function ReviewFoot({ onReject, onSkip, onApprove, isLast }: ReviewFootProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 24px",
        borderTop: `1px solid ${PALETTE.line}`,
        background: "#FBFBFB",
      }}
    >
      <div style={{ fontSize: 11.5, color: PALETTE.ink3, fontWeight: 500 }}>
        <Kbd>&larr;</Kbd> Previous
        <span style={{ margin: "0 10px" }}>&middot;</span>
        <Kbd>&rarr;</Kbd> Approve &amp; next
        <span style={{ margin: "0 10px" }}>&middot;</span>
        <Kbd>Esc</Kbd> Exit
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button
          type="button"
          onClick={onReject}
          style={{
            background: "transparent",
            border: 0,
            color: PALETTE.red,
            fontWeight: 500,
            fontSize: 13,
            padding: "10px 12px",
            cursor: "pointer",
          }}
        >
          Reject
        </button>
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: "transparent",
            border: 0,
            color: PALETTE.ink2,
            fontWeight: 500,
            fontSize: 13,
            padding: "10px 12px",
            cursor: "pointer",
          }}
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={onApprove}
          style={{
            background: "#0E1A1A",
            color: "#fff",
            border: 0,
            fontWeight: 600,
            fontSize: 13,
            padding: "10px 18px",
            borderRadius: 10,
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {isLast ? "Approve & finish" : "Approve & next"}
        </button>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        fontFamily: "inherit",
        background: "#E7EAF0",
        color: PALETTE.ink2,
        borderRadius: 4,
        padding: "1px 6px",
        fontWeight: 600,
        fontSize: 11,
        margin: "0 3px",
      }}
    >
      {children}
    </kbd>
  );
}